import { IconLayer } from '@deck.gl/layers';
import type { LayerState, LayerFeature, LinknycProps } from '@vitals/shared';
import { LAYER_LINKNYC } from '@vitals/shared';
import type { RendererDef } from './registry.js';
import { getKioskAtlas } from './linknycIcon.js';

interface KioskDatum {
  position: [number, number];
  /** Kiosk metadata, carried through for the hover tooltip (tooltip.ts). */
  props: LinknycProps;
}

/**
 * LinkNYC kiosks as standing monolith glyphs. Sized in meters so they grow as
 * you zoom in (clamped to a sensible pixel range), unlike a flat dot — a kiosk
 * reads as bigger the closer you get. The layer draws nothing until the sprite
 * atlas finishes rasterizing on the first frame.
 */
export function buildLinknycLayer(
  state: LayerState | undefined,
  _nowSec: number,
): IconLayer<KioskDatum> | null {
  if (!state || state.status !== 'ok' || state.features.length === 0) return null;

  const sprite = getKioskAtlas();
  if (!sprite) return null;

  const data: KioskDatum[] = (state.features as LayerFeature[]).map((f) => ({
    position: (f.geometry as { coordinates: [number, number] }).coordinates,
    props: f.props as unknown as LinknycProps,
  }));

  return new IconLayer<KioskDatum>({
    id: LAYER_LINKNYC,
    data,
    iconAtlas: sprite.url,
    iconMapping: sprite.mapping,
    getIcon: () => 'kiosk',
    getPosition: (d) => d.position,
    getSize: 130,
    sizeUnits: 'meters',
    sizeMinPixels: 14,
    sizeMaxPixels: 54,
    billboard: true,
    pickable: true,
  });
}

export const linknycRenderer: RendererDef = {
  layerId: LAYER_LINKNYC,
  build: (state, nowSec) => buildLinknycLayer(state, nowSec),
};
